import type { ReactNode } from "react";
import type { FinOpsInsightPanel, RiskLevel } from "@/types";

function SectionTitle({
  children,
  id,
  className = "",
}: {
  children: ReactNode;
  id?: string;
  className?: string;
}) {
  return (
    <h3
      id={id}
      className={`text-[11px] font-semibold uppercase tracking-[0.08em] text-(--text-tertiary) ${className}`}
    >
      {children}
    </h3>
  );
}

function PanelCard({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`rounded-xl border border-(--border-subtle) bg-(--bg-muted) p-4 shadow-(--shadow-card) ${className}`}
    >
      {children}
    </div>
  );
}

function riskStyles(level: RiskLevel) {
  switch (level) {
    case "high":
      return "bg-[color-mix(in_srgb,var(--risk-high)_18%,transparent)] text-[var(--risk-high)] ring-1 ring-[color-mix(in_srgb,var(--risk-high)_35%,transparent)]";
    case "moderate":
      return "bg-[color-mix(in_srgb,var(--risk-moderate)_18%,transparent)] text-[var(--risk-moderate)] ring-1 ring-[color-mix(in_srgb,var(--risk-moderate)_35%,transparent)]";
    default:
      return "bg-[color-mix(in_srgb,var(--risk-low)_18%,transparent)] text-[var(--risk-low)] ring-1 ring-[color-mix(in_srgb,var(--risk-low)_35%,transparent)]";
  }
}

function badgeStyles() {
  return "inline-flex items-center rounded-md px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide";
}

function ImpactEffort({
  impact,
  effort,
}: {
  impact: "high" | "medium" | "low";
  effort: "high" | "medium" | "low";
}) {
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      <span
        className={`${badgeStyles()} bg-(--accent-muted) text-(--accent)`}
      >
        Impact · {impact}
      </span>
      <span className={`${badgeStyles()} bg-background text-(--text-secondary) ring-1 ring-(--border-subtle)`}>
        Effort · {effort}
      </span>
    </div>
  );
}

function InsightPanelContent({ data }: { data: FinOpsInsightPanel }) {
  return (
    <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-4">
      <PanelCard className="border-(--accent)/25 bg-[linear-gradient(180deg,var(--bg-muted)_0%,var(--bg-elevated)_100%)]">
        <SectionTitle>Estimated monthly savings</SectionTitle>
        <p className="mt-3 text-3xl font-bold tracking-tight text-(--accent) tabular-nums">
          {data.estimatedMonthlySavings.amountLabel}
        </p>
        <p className="mt-2 text-xs leading-relaxed text-(--text-secondary)">
          {data.estimatedMonthlySavings.subtitle}
        </p>
        <ul className="mt-4 space-y-2 border-t border-(--border-subtle) pt-3">
          {data.estimatedMonthlySavings.breakdown.map((row) => (
            <li
              key={row.label}
              className="flex items-center justify-between gap-3 text-xs"
            >
              <span className="text-(--text-secondary)">{row.label}</span>
              <span className="font-semibold tabular-nums text-foreground">
                {row.value}
              </span>
            </li>
          ))}
        </ul>
      </PanelCard>

      {/*<PanelCard>*/}
      {/*  <div className="flex items-start justify-between gap-2">*/}
      {/*    <SectionTitle>Risk profile</SectionTitle>*/}
      {/*    <span*/}
      {/*      className={`${badgeStyles()} capitalize ${riskStyles(data.riskProfile.level)}`}*/}
      {/*    >*/}
      {/*      {data.riskProfile.level}*/}
      {/*    </span>*/}
      {/*  </div>*/}
      {/*  <p className="mt-3 text-sm leading-relaxed text-foreground">*/}
      {/*    {data.riskProfile.summary}*/}
      {/*  </p>*/}
      {/*  <ul className="mt-3 list-disc space-y-1.5 pl-4 text-xs text-(--text-secondary)">*/}
      {/*    {data.riskProfile.signals.map((s) => (*/}
      {/*      <li key={s}>{s}</li>*/}
      {/*    ))}*/}
      {/*  </ul>*/}
      {/*</PanelCard>*/}

      <PanelCard>
        <SectionTitle>Confidence level</SectionTitle>
        <div className="mt-3 flex items-baseline gap-2">
          <span className="text-2xl font-bold tabular-nums text-foreground">
            {data.confidenceLevel.percent}%
          </span>
          <span className="text-sm font-medium text-(--accent)">
            {data.confidenceLevel.label}
          </span>
        </div>
        <div
          className="mt-3 h-2 w-full overflow-hidden rounded-full bg-background ring-1 ring-(--border-subtle)"
          role="progressbar"
          aria-valuenow={data.confidenceLevel.percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Model confidence"
        >
          <div
            className="h-full rounded-full bg-brand-gradient transition-[width]"
            style={{ width: `${data.confidenceLevel.percent}%` }}
          />
        </div>
        <p className="mt-3 text-xs leading-relaxed text-(--text-secondary)">
          {data.confidenceLevel.rationale}
        </p>
      </PanelCard>

      {/*<PanelCard>*/}
      {/*  <SectionTitle>Detected context</SectionTitle>*/}
      {/*  <dl className="mt-3 space-y-3 text-xs">*/}
      {/*    <div>*/}
      {/*      <dt className="font-medium text-(--text-tertiary)">Cloud scope</dt>*/}
      {/*      <dd className="mt-1 leading-relaxed text-foreground">*/}
      {/*        {data.detectedContext.cloudScope}*/}
      {/*      </dd>*/}
      {/*    </div>*/}
      {/*    <div>*/}
      {/*      <dt className="font-medium text-(--text-tertiary)">*/}
      {/*        Billing cadence*/}
      {/*      </dt>*/}
      {/*      <dd className="mt-1 text-foreground">*/}
      {/*        {data.detectedContext.billingCadence}*/}
      {/*      </dd>*/}
      {/*    </div>*/}
      {/*    <div>*/}
      {/*      <dt className="font-medium text-(--text-tertiary)">*/}
      {/*        Primary services*/}
      {/*      </dt>*/}
      {/*      <dd className="mt-2 flex flex-wrap gap-1.5">*/}
      {/*        {data.detectedContext.primaryServices.map((s) => (*/}
      {/*          <span*/}
      {/*            key={s}*/}
      {/*            className="rounded-md bg-background px-2 py-0.5 text-[11px] font-medium text-foreground ring-1 ring-(--border-subtle)"*/}
      {/*          >*/}
      {/*            {s}*/}
      {/*          </span>*/}
      {/*        ))}*/}
      {/*      </dd>*/}
      {/*    </div>*/}
      {/*    <div>*/}
      {/*      <dt className="font-medium text-(--text-tertiary)">Org hints</dt>*/}
      {/*      <dd className="mt-2 space-y-1.5">*/}
      {/*        {data.detectedContext.orgHints.map((h) => (*/}
      {/*          <p key={h} className="leading-relaxed text-(--text-secondary)">*/}
      {/*            {h}*/}
      {/*          </p>*/}
      {/*        ))}*/}
      {/*      </dd>*/}
      {/*    </div>*/}
      {/*  </dl>*/}
      {/*</PanelCard>*/}

      {/*<PanelCard>*/}
      {/*  <SectionTitle id="top-opportunities">Top opportunities</SectionTitle>*/}
      {/*  <ol className="mt-3 space-y-3" aria-labelledby="top-opportunities">*/}
      {/*    {data.topOpportunities.map((opp, i) => (*/}
      {/*      <li*/}
      {/*        key={opp.id}*/}
      {/*        className="rounded-lg border border-(--border-subtle) bg-background p-3"*/}
      {/*      >*/}
      {/*        <div className="flex gap-2">*/}
      {/*          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-(--accent-muted) text-[11px] font-bold text-(--accent)">*/}
      {/*            {i + 1}*/}
      {/*          </span>*/}
      {/*          <div className="min-w-0 flex-1">*/}
      {/*            <p className="text-sm font-semibold text-foreground">*/}
      {/*              {opp.title}*/}
      {/*            </p>*/}
      {/*            <ImpactEffort impact={opp.impact} effort={opp.effort} />*/}
      {/*            <p className="mt-2 text-xs leading-relaxed text-(--text-secondary)">*/}
      {/*              {opp.detail}*/}
      {/*            </p>*/}
      {/*          </div>*/}
      {/*        </div>*/}
      {/*      </li>*/}
      {/*    ))}*/}
      {/*  </ol>*/}
      {/*</PanelCard>*/}

      <PanelCard>
        <SectionTitle>Assumptions</SectionTitle>
        <ul className="mt-3 list-disc space-y-1.5 pl-4 text-xs text-(--text-secondary)">
          {data.assumptionsAndConstraints.assumptions.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
        <SectionTitle id="constraints-heading" className="mt-5 block">
          Constraints
        </SectionTitle>
        <ul
          className="mt-3 list-disc space-y-1.5 pl-4 text-xs text-(--text-secondary)"
          aria-labelledby="constraints-heading"
        >
          {data.assumptionsAndConstraints.constraints.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
      </PanelCard>
    </div>
  );
}

type InsightPanelProps = {
  data: FinOpsInsightPanel;
};

export function InsightPanel({ data }: InsightPanelProps) {
  return (
    <aside className="hidden w-[min(100%,22rem)] shrink-0 flex-col border-l border-(--border-subtle) bg-(--bg-elevated) shadow-(--shadow-card) lg:flex xl:w-96">
      <div className="border-b border-(--border-subtle) px-4 py-4">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-(--accent)">
          Assessment insights
        </p>
        <h2 className="mt-1 text-sm font-semibold text-foreground">
          FinOps findings
        </h2>
        <p className="mt-1 text-xs leading-relaxed text-(--text-secondary)">
          Live view will bind to your models and billing exports. This panel
          mirrors the assessment workflow: savings, risk, confidence, and scope.
        </p>
      </div>
      <InsightPanelContent data={data} />
    </aside>
  );
}
